import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import {
    PrepPack,
    PrepPackReportV2,
    PrepPackDoc,
    DocBlock,
    DocSubsection,
    DocSection,
    SourceIndexEntry,
    PrepPackExpert,
    prepPackFocus,
} from "../../pages/sales/prepPacks/types";
import { formatMonthYear } from "../../utils";
import { styles } from "./pdfStyles";
import tdcLogo from "../../assets/logo-transparent-tdc-only.png";
import {
    Bullet,
    H2,
    H3,
    KeepTogether,
    Paragraph,
    SectionPage,
    Footer,
    PdpAuditAppendix,
    padCode,
    SourceEntry,
} from "./PrepPackPdf";

// v2 (engine-parity) renderer: the report is a typed block tree
// (PrepPackDoc) with citation numbers and the Source Index precomputed by
// backend code. Rendering is a generic dispatch over block types, so
// content/structure changes land server-side without portal edits (only a
// NEW block type needs a renderer here).

const CANONICAL_SECTION_NUMBER: Record<string, number> = {
    executive_summary: 1,
    company_and_brand_profile: 2,
    decision_maker_intelligence: 3,
    category_landscape: 4,
    voice_of_customer: 5,
    meeting_game_plan: 6,
    tdc_package_recommendation: 7,
};

type Citations = Record<string, number>;

// '[3][7]' marker for a cites list; unresolved keys silently dropped
// (backend validator guarantees resolution — this is belt-and-suspenders).
const citeMarks = (cites: string[] | undefined, citations: Citations) => {
    const nums = (cites || [])
        .filter((k) => citations[k] !== undefined)
        .map((k) => citations[k]);
    if (!nums.length) return "";
    return ` ${nums.map((n) => `[${n}]`).join("")}`;
};

// Generic table for the row-based block types. Columns carry a citations
// flag so the Ref column renders assigned [N] markers.
interface Col {
    header: string;
    width?: number;
    field: string;
    isRef?: boolean;
}

const DocTable = ({
    cols,
    rows,
    citations,
}: {
    cols: Col[];
    rows: Array<Record<string, any>>;
    citations: Citations;
}) => {
    if (!rows || rows.length === 0) return <Text style={styles.muted}>—</Text>;
    return (
        <>
            <View style={styles.tableHead} wrap={false}>
                {cols.map((c) => (
                    <Text
                        key={c.header}
                        style={[
                            styles.tableHeadCell,
                            c.width ? { width: c.width } : { flex: 1 },
                        ]}
                    >
                        {c.header}
                    </Text>
                ))}
            </View>
            {rows.map((r, i) => {
                const isLast = i === rows.length - 1;
                return (
                    <View
                        key={i}
                        style={[
                            styles.tableRow,
                            ...(i % 2 === 1 ? [styles.tableRowAlt] : []),
                            ...(isLast ? [styles.tableRowLast] : []),
                        ]}
                        wrap={false}
                    >
                        {cols.map((c) => (
                            <Text
                                key={c.header}
                                style={[
                                    styles.tableCell,
                                    c.width ? { width: c.width } : { flex: 1 },
                                ]}
                            >
                                {c.isRef
                                    ? citeMarks(r.cites, citations).trim()
                                    : String(r[c.field] === undefined || r[c.field] === null ? "" : r[c.field])}
                            </Text>
                        ))}
                    </View>
                );
            })}
        </>
    );
};

const DocBlockView = ({
    block,
    citations,
}: {
    block: DocBlock;
    citations: Citations;
}) => {
    switch (block.type) {
        case "prose":
            return (
                <Text style={styles.paragraph}>
                    {block.text}
                    {citeMarks(block.cites, citations)}
                </Text>
            );
        case "bullets":
            return (
                <>
                    {block.items.map((item, i) => (
                        <Bullet key={i}>
                            {item}
                            {i === block.items.length - 1
                                ? citeMarks(block.cites, citations)
                                : ""}
                        </Bullet>
                    ))}
                </>
            );
        case "inference":
            return (
                <Text style={styles.paragraph}>
                    <Text style={styles.namedItemTitle}>Inference: </Text>
                    {block.text}
                </Text>
            );
        case "callout":
            // NOTE: quoteBlock is a TEXT style carrying fontStyle italic —
            // putting it on the wrapping View makes nested Lato-Bold text
            // inherit italic, which that family has no source for (render
            // hard-fails). Keep the container style layout-only.
            return (
                <View style={styles.namedItem} wrap={false}>
                    <Text style={styles.namedItemTitle}>{block.label}</Text>
                    <Text style={styles.namedItemBody}>
                        {block.text}
                        {citeMarks(block.cites, citations)}
                    </Text>
                </View>
            );
        case "cheat_sheet":
            return (
                <>
                    <KeepTogether>
                        <H3>Lead line</H3>
                        <Text style={styles.quoteBlock}>{block.lead_line}</Text>
                    </KeepTogether>
                    <KeepTogether>
                        <H3>Three points to lead with</H3>
                        {block.three_points.map((p, i) => (
                            <View key={i} style={styles.namedItem} wrap={false}>
                                <Text style={styles.namedItemTitle}>{p.label}</Text>
                                <Text style={styles.namedItemBody}>
                                    {p.body}
                                    {citeMarks(p.cites, citations)}
                                </Text>
                            </View>
                        ))}
                    </KeepTogether>
                    <KeepTogether>
                        <H3>Top objections and the one-line answer</H3>
                        {block.top_objections.map((o, i) => (
                            <View key={i} style={styles.objectionBlock} wrap={false}>
                                <Text style={styles.objectionQuote}>{o.objection}</Text>
                                <Text style={styles.objectionResponseLabel}>Answer</Text>
                                <Text style={styles.objectionResponseBody}>
                                    {o.answer}
                                    {citeMarks(o.cites, citations)}
                                </Text>
                            </View>
                        ))}
                    </KeepTogether>
                    <KeepTogether>
                        <H3>The recommendation</H3>
                        <Paragraph>{block.recommendation}</Paragraph>
                    </KeepTogether>
                    <KeepTogether>
                        <H3>The ask</H3>
                        <Paragraph>{block.ask}</Paragraph>
                    </KeepTogether>
                </>
            );
        case "at_a_glance":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Attribute", field: "attribute", width: 150 },
                        { header: "Detail", field: "detail" },
                        { header: "Ref", field: "cites", width: 40, isRef: true },
                    ]}
                    rows={block.rows}
                />
            );
        case "competitive_set":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Brand", field: "brand", width: 100 },
                        { header: "Position", field: "position", width: 120 },
                        { header: "Notable", field: "notable" },
                        { header: "Ref", field: "cites", width: 40, isRef: true },
                    ]}
                    rows={block.rows}
                />
            );
        case "pain_point_library":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Theme", field: "theme", width: 80 },
                        { header: "Pain Point (verbatim)", field: "pain_point" },
                        { header: "Persona", field: "persona", width: 80 },
                        { header: "Scope", field: "scope", width: 80 },
                        { header: "Freq", field: "freq", width: 40 },
                        { header: "Ref", field: "cites", width: 40, isRef: true },
                    ]}
                    rows={block.rows}
                />
            );
        case "top_themes":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Theme", field: "theme", width: 100 },
                        { header: "The Insight", field: "insight" },
                        { header: "Best Messaging Angle", field: "angle" },
                    ]}
                    rows={block.rows}
                />
            );
        case "consumer_objections":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Objection (customer voice)", field: "objection" },
                        { header: "Honest Counter-Message", field: "counter_message" },
                    ]}
                    rows={block.rows}
                />
            );
        case "activation_map":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Theme", field: "theme", width: 100 },
                        { header: "Funnel Stage", field: "funnel_stage", width: 80 },
                        { header: "Best TDC Content Type", field: "content_type" },
                        { header: "Best Channels", field: "channels" },
                    ]}
                    rows={block.rows}
                />
            );
        case "copy_bank":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Customer Phrase (verbatim)", field: "phrase" },
                        { header: "Ref", field: "cites", width: 40, isRef: true },
                    ]}
                    rows={block.rows}
                />
            );
        case "objections_responses":
            return (
                <>
                    {block.rows.map((o, i) => (
                        <View key={i} style={styles.objectionBlock} wrap={false}>
                            <Text style={styles.objectionQuote}>{o.objection}</Text>
                            <Text style={styles.objectionResponseLabel}>Response</Text>
                            <Text style={styles.objectionResponseBody}>
                                {o.response}
                                {citeMarks(o.cites, citations)}
                            </Text>
                        </View>
                    ))}
                </>
            );
        case "discovery_questions":
            return (
                <>
                    {block.questions.map((q, i) => (
                        <Bullet key={i}>{`${i + 1}. ${q}`}</Bullet>
                    ))}
                </>
            );
        case "proof_points":
            return (
                <>
                    {block.points.map((p, i) => (
                        <Bullet key={i}>
                            {`${i + 1}. ${p.point}`}
                            {citeMarks(p.cites, citations)}
                        </Bullet>
                    ))}
                </>
            );
        case "asset_list":
            return (
                <>
                    {block.assets.map((a, i) => (
                        <View key={i} style={styles.namedItem} wrap={false}>
                            <Text style={styles.namedItemTitle}>{a.name}</Text>
                            <Text style={styles.namedItemBody}>{a.description}</Text>
                            <Text style={styles.muted}>
                                Formats: {(a.formats || []).join(", ")}
                            </Text>
                        </View>
                    ))}
                </>
            );
        case "funnel_stage_map":
            return (
                <DocTable
                    citations={citations}
                    cols={[
                        { header: "Funnel Stage", field: "funnel_stage", width: 80 },
                        { header: "Objective", field: "objective" },
                        { header: "Asset Type", field: "asset_type", width: 110 },
                        { header: "Placement", field: "placement", width: 90 },
                    ]}
                    rows={block.rows}
                />
            );
        default:
            return <Text style={styles.muted}>[unsupported block]</Text>;
    }
};

const DocSubsectionView = ({
    subsection,
    citations,
}: {
    subsection: DocSubsection;
    citations: Citations;
}) => (
    <>
        <H2>
            {subsection.number} {subsection.title}
        </H2>
        {subsection.blocks.map((b, i) => (
            <DocBlockView key={i} block={b} citations={citations} />
        ))}
        {(subsection.deeper_subsections || []).map((deep, i) => (
            <React.Fragment key={i}>
                <H3>{deep.title}</H3>
                {deep.blocks.map((b, j) => (
                    <DocBlockView key={j} block={b} citations={citations} />
                ))}
            </React.Fragment>
        ))}
    </>
);

const DocSectionPage = ({
    section,
    citations,
    code,
}: {
    section: DocSection;
    citations: Citations;
    code: string;
}) => {
    const n = CANONICAL_SECTION_NUMBER[section.id];
    const title = n ? `${n}. ${section.title}` : section.title;
    return (
        <SectionPage title={title} code={code}>
            {section.intro_blocks.map((b, i) => (
                <DocBlockView key={i} block={b} citations={citations} />
            ))}
            {section.subsections.map((sub, i) => (
                <DocSubsectionView key={i} subsection={sub} citations={citations} />
            ))}
        </SectionPage>
    );
};

const SourceGroup = ({ entries }: { entries: SourceIndexEntry[] }) => (
    <>
        {entries.map((s) => (
            <SourceEntry
                key={s.n}
                n={s.n}
                title={`${s.publication} - ${s.title}`}
                url={s.url}
            />
        ))}
    </>
);

const expertInitials = (name: string) =>
    (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("") || "?";

const ExpertsPage = ({
    experts,
    code,
    expertImages,
}: {
    experts: PrepPackExpert[];
    code: string;
    // userid -> data URL, pre-fetched by the ZIP builder. Raw remote URLs
    // are never handed to <Image> — one dead avatar URL would fail the
    // whole render; missing entries fall back to an initials circle.
    expertImages: Record<number, string>;
}) => (
    <SectionPage title="Recommended Experts" code={code}>
        <Paragraph>
            A one-per-archetype bench curated from TDC's expert roster via
            Expert IQ category matching — different kinds of experts, each
            speaking to a different shopper objection or product line.
        </Paragraph>
        {experts.map((e) => {
            const img = expertImages[e.userid];
            return (
                <View key={e.userid} style={styles.expertRow} wrap={false}>
                    {img ? (
                        <Image src={img} style={styles.expertAvatar} />
                    ) : (
                        <View style={styles.expertInitials}>
                            <Text style={styles.expertInitialsText}>
                                {expertInitials(e.name)}
                            </Text>
                        </View>
                    )}
                    <View style={styles.expertBody}>
                        <Text style={styles.namedItemTitle}>
                            {e.rank}. {e.name}
                            {e.profession ? ` — ${e.profession}` : ""}
                        </Text>
                        <Text style={styles.namedItemBody}>{e.whyFits}</Text>
                        {e.expertIq && e.expertIq.phrase ? (
                            <Text style={styles.muted}>
                                Expert IQ: {e.expertIq.phrase}
                            </Text>
                        ) : null}
                    </View>
                </View>
            );
        })}
    </SectionPage>
);

interface DocPdfProps {
    pack: PrepPack;
    report: PrepPackReportV2;
    internal?: boolean;
    expertImages?: Record<number, string>;
}

export function PrepPackDocPdf({
    pack,
    report,
    internal = true,
    expertImages = {},
}: DocPdfProps) {
    const doc: PrepPackDoc = report.doc;
    const code = doc.meta.run_id || padCode(pack.id);
    const citations = report.citations || {};
    const correction = report.parentCorrection || ({ detected: false } as any);
    const dateLabel =
        formatMonthYear(doc.meta.generated_at) || formatMonthYear(pack.created_at);
    const hasAudits = (pack.audits || []).some((a) => a.report);
    const experts = report.experts || [];
    const qa = report.qa;

    const toc = [
        ...doc.sections.map((s) => {
            const n = CANONICAL_SECTION_NUMBER[s.id];
            return n ? `${n}. ${s.title}` : s.title;
        }),
        ...(experts.length ? ["Recommended Experts"] : []),
        "Source Index",
        ...(hasAudits ? ["Appendix: PDP Audit Snapshot"] : []),
    ];

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.coverInner}>
                    <Image src={tdcLogo} style={styles.coverLogo} />
                    <Text style={styles.coverTitle}>
                        {doc.meta.parent_company} Brand Intelligence Report
                    </Text>
                    <Text style={styles.coverSubtitle}>
                        {doc.meta.brand}
                        {prepPackFocus(pack) ? ` · ${prepPackFocus(pack)}` : ""}
                    </Text>
                    <View style={styles.coverDivider} />
                    <Text style={styles.coverMetaPrimary}>
                        Prepared for: {doc.meta.prepared_for || "The Desire Company Sales Team"}
                    </Text>
                    <Text style={styles.coverMeta}>
                        Prepared by: {doc.meta.prepared_by || "TDC Expert and Strategy Team"}
                    </Text>
                    <Text style={styles.coverMeta}>{dateLabel}</Text>
                    {internal && qa ? (
                        <Text style={styles.coverMeta}>
                            QA verdict: {qa.verdict}
                            {report.warnings && report.warnings.length
                                ? ` — warnings: ${report.warnings.join(", ")}`
                                : ""}
                        </Text>
                    ) : null}
                </View>
                <Footer code={code} />
            </Page>

            <SectionPage
                title={
                    correction.detected
                        ? "Important: Parent-Company Correction"
                        : "Report Context"
                }
                code={code}
                bookmark={false}
            >
                {correction.detected ? (
                    <>
                        <View style={styles.namedItem} wrap={false}>
                            <Text style={styles.namedItemTitle}>
                                Critical flag for the rep
                            </Text>
                            <Text style={styles.namedItemBody}>
                                {`The intake listed the prospect as '${correction.input}', but ${doc.parent_block.brand_in_scope} belongs to ${correction.corrected}. This document is prepared for a ${correction.corrected} team call. Do not enter the meeting referring to the company as '${correction.input}'.`}
                            </Text>
                        </View>
                        {correction.reason ? (
                            <Paragraph>{correction.reason}</Paragraph>
                        ) : null}
                    </>
                ) : (
                    <Paragraph>
                        {`This report is prepared for a meeting with ${doc.parent_block.parent_name} regarding ${doc.parent_block.brand_in_scope}.`}
                    </Paragraph>
                )}
                <H2>Table of Contents</H2>
                {toc.map((row) => (
                    <View key={row} style={styles.tocRow}>
                        <Text style={styles.tocLabel}>{row}</Text>
                        <View style={styles.tocFill} />
                    </View>
                ))}
            </SectionPage>

            {doc.sections.map((section) => (
                <DocSectionPage
                    key={section.id}
                    section={section}
                    citations={citations}
                    code={code}
                />
            ))}

            {experts.length > 0 ? (
                <ExpertsPage experts={experts} code={code} expertImages={expertImages} />
            ) : null}

            <SectionPage title="Source Index" code={code}>
                <Paragraph>
                    Every factual claim in this report is cited to a numbered
                    source below.
                </Paragraph>
                {report.sourceIndex && report.sourceIndex.company.length > 0 ? (
                    <View>
                        <H2>Company, Contact, and Category Sources</H2>
                        <SourceGroup entries={report.sourceIndex.company} />
                    </View>
                ) : null}
                {report.sourceIndex && report.sourceIndex.reddit.length > 0 ? (
                    <View>
                        <H2>Voice of Customer Sources (Reddit)</H2>
                        <SourceGroup entries={report.sourceIndex.reddit} />
                    </View>
                ) : null}
            </SectionPage>

            <PdpAuditAppendix pack={pack} code={code} />
        </Document>
    );
}
